/**
 * シフトパターンAPI クライアント
 */
import { parseApiError } from "../utils/apiError";
import { authFetch, API_BASE_URL } from "./client";

/**
 * シフトパターンに紐付いたタグ情報（一覧埋め込み）
 */
export interface ShiftPatternTagInfo {
    id: number;
    name: string | null;
    color: string | null;
}

/**
 * シフトパターンレスポンスの型
 */
export interface ShiftPatternResponse {
    id: number;
    code: string;
    organization_id: number | null;
    name: string;
    short_name: string;
    work_start_time: string | null;
    work_end_time: string | null;
    break_time_minutes: number;
    description: string | null;
    tags: ShiftPatternTagInfo[];
    created_at: string | null;
    updated_at: string | null;
}

/**
 * シフトパターン一覧レスポンスの型
 */
export interface ShiftPatternListResponse {
    items: ShiftPatternResponse[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
}

/**
 * シフトパターン作成リクエストの型
 */
export interface ShiftPatternCreateRequest {
    code: string;
    organization_id?: number | null;
    name: string;
    short_name: string;
    work_start_time: string;
    work_end_time: string;
    break_time_minutes: number;
    description?: string | null;
}

/**
 * シフトパターン更新リクエストの型
 */
export interface ShiftPatternUpdateRequest {
    code?: string;
    organization_id?: number | null;
    name?: string;
    short_name?: string;
    work_start_time?: string;
    work_end_time?: string;
    break_time_minutes?: number;
    description?: string | null;
}

/**
 * シフトパターン一覧を取得
 */
export async function fetchShiftPatterns(params?: {
    page?: number;
    page_size?: number;
    keyword?: string;
    organization_id?: number;
}): Promise<ShiftPatternListResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.page_size) searchParams.set("page_size", String(params.page_size));
    if (params?.keyword) searchParams.set("keyword", params.keyword);
    if (params?.organization_id) searchParams.set("organization_id", String(params.organization_id));

    const queryString = searchParams.toString();
    const url = `${API_BASE_URL}/shift-patterns${queryString ? `?${queryString}` : ""}`;

    const response = await authFetch(url);

    if (!response.ok) {
        throw new Error(`Failed to fetch shift patterns: ${response.status}`);
    }

    return response.json();
}

/**
 * シフトパターン詳細を取得
 */
export async function fetchShiftPattern(id: number): Promise<ShiftPatternResponse> {
    const response = await authFetch(`${API_BASE_URL}/shift-patterns/${id}`);

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error("シフトパターンが見つかりません");
        }
        throw new Error(`Failed to fetch shift pattern: ${response.status}`);
    }

    return response.json();
}

/**
 * シフトパターンを作成
 */
export async function createShiftPattern(data: ShiftPatternCreateRequest): Promise<ShiftPatternResponse> {
    const response = await authFetch(`${API_BASE_URL}/shift-patterns`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw await parseApiError(response, "シフトパターンの作成に失敗しました");
    }

    return response.json();
}

/**
 * シフトパターンを更新
 */
export async function updateShiftPattern(id: number, data: ShiftPatternUpdateRequest): Promise<ShiftPatternResponse> {
    const response = await authFetch(`${API_BASE_URL}/shift-patterns/${id}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw await parseApiError(response, "シフトパターンの更新に失敗しました");
    }

    return response.json();
}

/**
 * シフトパターンを削除（論理削除）
 */
export async function deleteShiftPattern(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE_URL}/shift-patterns/${id}`, {
        method: "DELETE",
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error("シフトパターンが見つかりません");
        }
        throw new Error(`Failed to delete shift pattern: ${response.status}`);
    }
}
